import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck, Cable, CheckCircle2, CircleDashed, Code2, Database,
  ExternalLink, KeyRound, Megaphone, MessageSquareText, MousePointerClick,
  RefreshCw, Search, Tag, Workflow,
} from "lucide-react";
import { supabase } from "./supabase";
import type { TrackingInventoryData, TrackingItem, TrackingStatus, TrackingSystemKey } from "./types";

const systemMeta: Record<TrackingSystemKey, { label: string; description: string; identifier: string; icon: typeof Database }> = {
  clarity: { label: "Microsoft Clarity", description: "Behavior analytics and session recording", identifier: "Project ID", icon: MousePointerClick },
  supabase: { label: "Supabase", description: "Authoritative property and lead data", identifier: "Property key", icon: Database },
  google_ads: { label: "Google Ads", description: "Paid search conversion attribution", identifier: "Customer ID", icon: Megaphone },
  meta_ads: { label: "Meta Ads", description: "Paid social conversion attribution", identifier: "Pixel or dataset ID", icon: Megaphone },
  zapier: { label: "Zapier", description: "Workflow handoffs and automations", identifier: "Zap or workspace ID", icon: Workflow },
  google_tag_manager: { label: "Google Tag Manager", description: "Site container and tag deployment", identifier: "GTM container ID", icon: Tag },
  data_id: { label: "Data ID", description: "Site identity and routing key", identifier: "Data ID", icon: Code2 },
  email_popup: { label: "Email popup", description: "On-site email conversion capture", identifier: "Form or popup ID", icon: MousePointerClick },
  text_popup: { label: "Text popup", description: "On-site SMS conversion capture", identifier: "Popup or campaign ID", icon: MessageSquareText },
  blue_text_add_on: { label: "Blue text add-on", description: "Blue-text enhancement status", identifier: "Configuration ID", icon: Cable },
};

const statusLabels: Record<TrackingStatus, string> = {
  connected: "Connected",
  partial: "Needs attention",
  missing: "Missing",
  not_applicable: "Not applicable",
};

function TrackingEditor({ item, onSaved }: { item: TrackingItem; onSaved: (item: TrackingItem) => void }) {
  const meta = systemMeta[item.system_key];
  const Icon = meta.icon;
  const [status, setStatus] = useState(item.status);
  const [identifier, setIdentifier] = useState(item.identifier || "");
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setStatus(item.status);
    setIdentifier(item.identifier || "");
    setNotes(item.notes || "");
    setMessage("");
  }, [item]);

  const save = async () => {
    setSaving(true);
    setMessage("");
    const result = await supabase.rpc("dashboard_tracking_upsert", {
      p_property_id: item.property_id,
      p_system_key: item.system_key,
      p_status: status,
      p_identifier: identifier.trim() || null,
      p_details: item.details || {},
      p_notes: notes.trim() || null,
    });
    if (result.error) setMessage(result.error.message);
    else if (result.data) {
      onSaved({ ...item, ...(result.data as TrackingItem), sort_order: item.sort_order });
      setMessage("Saved");
    }
    setSaving(false);
  };

  return <article className={`tracking-item tracking-item--${status}`}>
    <header><span><Icon /><i /></span><div><strong>{meta.label}</strong><small>{meta.description}</small></div></header>
    <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value as TrackingStatus)}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
    <label><span>{meta.identifier}</span><input value={identifier} onChange={(event) => setIdentifier(event.target.value)} placeholder={`Add ${meta.identifier.toLowerCase()}`} /></label>
    <label><span>Notes</span><textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Owner, blocker, next step, or verification detail" rows={3} /></label>
    <footer><span>{item.verified_at ? <>Verified {new Date(item.verified_at).toLocaleDateString()}</> : "Not verified"}</span><button className="button button--quiet" onClick={() => void save()} disabled={saving}>{saving ? <RefreshCw className="spin" /> : <BadgeCheck />} Save</button></footer>
    {message && <p className={message === "Saved" ? "tracking-success" : "form-error"}>{message}</p>}
  </article>;
}

export function TrackingInventory() {
  const [data, setData] = useState<TrackingInventoryData | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (propertyId?: number) => {
    setLoading(true);
    setError("");
    const result = await supabase.rpc("dashboard_tracking_inventory", { p_property_id: propertyId || null });
    if (result.error) setError(result.error.message);
    else if (result.data) setData(result.data as TrackingInventoryData);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const selected = data?.properties.find((property) => property.property_id === data.selected_property_id);
  const filteredProperties = useMemo(() => data?.properties.filter((property) =>
    `${property.property_name} ${property.domain || ""}`.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 10) || [], [data, query]);
  const portfolioCoverage = useMemo(() => {
    const total = data?.properties.reduce((sum, property) => sum + property.total_systems, 0) || 0;
    const connected = data?.properties.reduce((sum, property) => sum + property.connected_systems, 0) || 0;
    return { total, connected, percentage: total ? Math.round((connected / total) * 100) : 0 };
  }, [data]);

  const replaceItem = (saved: TrackingItem) => setData((current) => current ? {
    ...current,
    items: current.items.map((item) => item.system_key === saved.system_key ? saved : item),
    properties: current.properties.map((property) => property.property_id !== saved.property_id ? property : {
      ...property,
      connected_systems: current.items.filter((item) => item.system_key !== saved.system_key ? item.status === "connected" : saved.status === "connected").length,
      partial_systems: current.items.filter((item) => item.system_key !== saved.system_key ? item.status === "partial" : saved.status === "partial").length,
      missing_systems: current.items.filter((item) => item.system_key !== saved.system_key ? item.status === "missing" : saved.status === "missing").length,
    }),
  } : current);

  if (loading && !data) return <section className="section tracking-loading"><RefreshCw className="spin" /><span>Opening private tracking inventory…</span></section>;

  return <div className="tracking-page">
    <section className="tracking-intro">
      <div><KeyRound /><span><h2>Private tracking ledger</h2><p>Only super admins can read or change this inventory. Every other role is blocked in Supabase.</p></span></div>
      <div className="coverage-meter"><span><strong>{portfolioCoverage.percentage}%</strong> portfolio coverage</span><div><i style={{ width: `${portfolioCoverage.percentage}%` }} /></div><small>{portfolioCoverage.connected} of {portfolioCoverage.total} system checks connected</small></div>
    </section>

    <section className="tracking-selector" aria-labelledby="tracking-property-title">
      <div><h2 id="tracking-property-title">Choose a property</h2><p>Inspect one site’s full instrumentation stack.</p></div>
      <label className="search"><Search /><span className="sr-only">Search properties</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search property or domain" /></label>
      {query && <div className="tracking-results">{filteredProperties.map((property) => <button key={property.property_id} onClick={() => { setQuery(""); void load(property.property_id); }}>
        <span><strong>{property.property_name}</strong><small>{property.domain || "No domain"}</small></span><span>{property.connected_systems}/{property.total_systems}<small>connected</small></span>
      </button>)}{!filteredProperties.length && <p>No properties match that search.</p>}</div>}
    </section>

    {error && <section className="section tracking-error"><CircleDashed /><div><strong>Tracking inventory unavailable</strong><span>{error}</span></div><button className="button button--quiet" onClick={() => void load(data?.selected_property_id)}>Try again</button></section>}

    {data && selected && <>
      <section className="tracking-property-head">
        <div><span className="property-monogram">{selected.property_name.slice(0, 2).toUpperCase()}</span><span><h2>{selected.property_name}</h2>{selected.domain ? <a href={`https://${selected.domain}`} target="_blank" rel="noreferrer">{selected.domain}<ExternalLink /></a> : <small>No domain assigned</small>}</span></div>
        <div className="tracking-totals"><span><CheckCircle2 /><strong>{selected.connected_systems}</strong><small>connected</small></span><span><CircleDashed /><strong>{selected.partial_systems}</strong><small>attention</small></span><span><Cable /><strong>{selected.missing_systems}</strong><small>missing</small></span></div>
      </section>
      <section className={`tracking-grid ${loading ? "is-loading" : ""}`} aria-label={`${selected.property_name} tracking systems`}>
        {data.items.map((item) => <TrackingEditor key={item.system_key} item={item} onSaved={replaceItem} />)}
      </section>
    </>}
  </div>;
}
